import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from "@angular/core";
import {SelectionType, SelectionTypes} from "../../../../static/selection-types";
import {DeleteToFromCapacity, TransportOrder} from "../../../../pb-screen/models/transport-order/transport-order.interface";
import {PlanningBlock} from "../../../../pb-screen/models/planning-block/planning-block.interface";
import {OrderLine} from "../../../../pb-screen/models/order-line/orderLine.interface";
import {ButtonsIDEnum} from "../../../models/buttons-visibility";
import {SimpleModalService} from "ngx-simple-modal";
import {UserFeedbackService} from "../../../../services/user-feedback.service";
import {PlanningActionService} from "../../../services/planningaction.service";
import {ButtonVisibilityService} from "../../../services/button-visibility.service";
import {TranslateService} from "@ngx-translate/core";
import {CLOSED, IN_PROGRESS, PLANNED, PRE_PLANNED} from "../../../models/status-type.interface";
import {
  PlanningactionsConfirmationModalComponent,
  PlanningactionsConfirmationModalResult
} from "../planningactions-confirmation-modal/planningactions-confirmation-modal.component";
import {PlanningBoardFacade} from "../../../../planningBoard/services/planning-board.facade";
import {
  PlanningactionsReplanModalComponent,
  ReplanNewToPlanningRequest,
  ReplanModalResult
} from "../planningactions-replan-modal/planningactions-replan-modal.component";
import {catchError, concatMap, finalize, map, tap} from "rxjs/operators";
import {forkJoin, Observable, of, Subscription} from "rxjs";
import {PbActions} from "../../../../pb-screen/pb.actions";
import {PbScreenService} from "../../../../pb-screen/services/pb-screen.service";
import {CalculationStateService} from "../../../../optimistic-planning/calculation-state.service";
import {CalculationResult} from "../../../../optimistic-planning/calculation-state.models";
import {MsalService} from "../../../../msal/msal.service";
import {environment} from "../../../../../environments/environment";
import {mapPbsToPlanningBlockDetails} from "../../../../optimistic-planning/pb-details.mapper";

enum ActionsStatusResult {
  BadSelection,
  FailedPreConditions,
  Failure,
  Success,
}

export interface PlanningActionResult {
  source: "" | string,
  transportOrders?: [] | TransportOrder[],
  planningBlocks?: [] | PlanningBlock[]
  deletedCapacity?: [] | DeleteToFromCapacity[]
}

@Component({
  selector: "planningactions-menu",
  templateUrl: "./planningactions-menu.component.html",
  styleUrls: ["./planningactions-menu.component.scss"],
  host: {
    "[class.planningactions-menu--single-column]": "isSingleColumnLayout()",
    "[class.planningactions-menu--two-columns]": "!isSingleColumnLayout()"
  }
})
export class PlanningactionsMenuComponent implements OnChanges, OnDestroy {
  @Input() onStart?: () => void;
  @Input() selectionType: SelectionType = SelectionTypes.TransportOrder;
  @Input() selectedOrderLines?: OrderLine[];
  @Input() selectedPlanningBlocks?: PlanningBlock[];
  @Input() selectedTransportOrders?: TransportOrder[];

  @Output() refreshRequested =
    new EventEmitter<PlanningActionResult>();
  @Output() closeRequested = new EventEmitter<void>();

  readonly ButtonsIDEnum = ButtonsIDEnum;
  readonly SelectionTypes = SelectionTypes;
  readonly stickyToaster =
    {
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: true,
      tapToDismiss: true
    };

  resolvedTransportOrders: TransportOrder[] = [];
  private resolveToSub?: Subscription;

  constructor(
    private readonly simpleModalService: SimpleModalService,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly planningActionService: PlanningActionService,
    private readonly buttonsVisibilityService: ButtonVisibilityService,
    private readonly translateService: TranslateService,
    private readonly planningBoardFacade: PlanningBoardFacade,
    private readonly pbActions: PbActions,
    private readonly pbService: PbScreenService,
    private readonly calcStateService: CalculationStateService,
    private readonly authService: MsalService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedPlanningBlocks || changes.selectionType) {
      this.resolveTransportOrdersForSelectedPBs();
    }
  }

  ngOnDestroy(): void {
    this.resolveToSub?.unsubscribe();
  }

  private resolveTransportOrdersForSelectedPBs(): void {
    this.resolveToSub?.unsubscribe();
    this.resolvedTransportOrders = [];

    if (this.selectionType !== SelectionTypes.PlanningBlock || !this.selectedPlanningBlocks?.length) {
      return;
    }

    const uniqueToIds = [...new Set(
      this.selectedPlanningBlocks
        .map(pb => pb.transportOrderNumber)
        .filter(id => !!id)
    )];

    if (!uniqueToIds.length) {
      return;
    }

    const fetches = uniqueToIds.map(toId =>
      this.planningActionService.getTransportOrderById(toId)
    );

    this.resolveToSub = forkJoin(fetches).subscribe(results => {
      this.resolvedTransportOrders = results.filter(to => !!to) as TransportOrder[];
    });
  }

  private _preCancelTOs: TransportOrder[] | null = null;

  private get isOptimistic(): boolean {
    return !!(environment as any).enableOptimisticPlanning;
  }

  private getSlotContext(): { truckId?: string; truckName?: string; driverName?: string } {
    const pb = this.selectedPlanningBlocks?.[0];
    return { truckId: pb?.truckId, truckName: pb?.truck, driverName: pb?.driver?.name };
  }

  private startPbOperation(operationType: string, pbs: PlanningBlock[]): string | null {
    if (!this.isOptimistic) return null;
    const slot = this.getSlotContext();
    const account = this.authService.getAccount();
    // Capture operation count before — startCalculation prepends the new
    // operation to the BehaviorSubject synchronously before the HTTP call,
    // so we can read its localOpId from state immediately after subscribe().
    const opsBefore = this.calcStateService.operations.length;
    this.calcStateService.startCalculation({
      planningBlockIds: pbs.map(p => String(p.id)),
      userId: account?.localAccountId || '',
      userName: account?.name || '',
      truckName: slot.truckName,
      driverName: slot.driverName,
      operationType,
      planningBlockDetails: mapPbsToPlanningBlockDetails(pbs)
    }).subscribe();
    const ops = this.calcStateService.operations;
    return ops.length > opsBefore ? ops[0].operationId : null;
  }

  private startToOperation(operationType: string, tos: TransportOrder[]): string | null {
    if (!this.isOptimistic || !tos?.length) return null;
    const to = tos[0];
    const account = this.authService.getAccount();
    const pbIds = tos.flatMap(t => (t.planningBlocks || []).map(pb => String(pb.id)));
    const opsBefore = this.calcStateService.operations.length;
    this.calcStateService.startCalculation({
      planningBlockIds: pbIds.length ? pbIds : [String(to.id)],
      userId: account?.localAccountId || '',
      userName: account?.name || '',
      truckName: to.truck?.name || (to as any).truckNo || '',
      driverName: (to as any).driver?.name || (to as any).driver || '',
      operationType,
      planningBlockDetails: mapPbsToPlanningBlockDetails(tos.flatMap(t => t.planningBlocks || []))
    }).subscribe();
    const ops = this.calcStateService.operations;
    return ops.length > opsBefore ? ops[0].operationId : null;
  }

  private completePbOperation(opId: string, headline: string): void {
    if (!opId) return;
    const result: CalculationResult = {
      success: true,
      summary: { headline }
    };
    this.calcStateService.completeCalculation(opId, result).subscribe();
  }

  private failPbOperation(opId: string, errorMsg: string): void {
    if (!opId) return;
    this.calcStateService.failCalculation(opId, errorMsg).subscribe();
  }

  /** Extract a user-friendly error message from an HTTP error response */
  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.error?.feedback?.[0]?.description) return err.error.feedback[0].description;
    if (err?.error?.exceptionMessage) return err.error.exceptionMessage;
    if (err?.error?.message) return err.error.message;
    if (typeof err?.error === 'string' && err.error.length < 300) return err.error;
    if (Array.isArray(err?.error)) return err.error.map(e => e?.feedback?.[0]?.description).filter(Boolean).join('; ');
    return fallback;
  }


  translate(key: string, params?: object): string {
    return this.translateService.instant(key, params);
  }

  isButtonVisible(buttonId: ButtonsIDEnum) {
    return !this.buttonsVisibilityService.isButtonHidden(buttonId);
  }

  private toHasPlanningBlocks(to?: TransportOrder): boolean {
    if (!to || !to.planningBlocks || to.planningBlocks.length === 0) {
      return false;
    }
    return to.planningBlocks && to.planningBlocks.length > 0;
  }

  private hasPBStatuses(expectedStatusTypes: number[], requireAll: boolean): boolean {
    const blocks = this.selectedPlanningBlocks || [];
    if (!blocks.length && this.selectedTransportOrders && this.selectedTransportOrders.length) {
      // if selection is TO, validate via their PBs
      const allBlocks = this.selectedTransportOrders
        .filter(to => this.toHasPlanningBlocks(to))
        .flatMap(to => to.planningBlocks || [] as any);
      if (!allBlocks.length) {
        return false;
      }
      if (requireAll) {
        return allBlocks.every((pb: any) => expectedStatusTypes.includes(pb.statusType));
      }
      return allBlocks.some((pb: any) => expectedStatusTypes.includes(pb.statusType));
    }
    if (!blocks.length) {
      return false;
    }
    if (requireAll) {
      return blocks.every(pb => expectedStatusTypes.includes((pb as any).statusType));
    }
    return blocks.some(pb => expectedStatusTypes.includes((pb as any).statusType));
  }

  private hasResolvedTOPBStatuses(expectedStatusTypes: number[], requireAll: boolean): boolean {
    const allBlocks = this.resolvedTransportOrders
      .filter(to => this.toHasPlanningBlocks(to))
      .flatMap(to => to.planningBlocks || [] as any);
    if (!allBlocks.length) {
      return false;
    }
    if (requireAll) {
      return allBlocks.every((pb: any) => expectedStatusTypes.includes(pb.statusType));
    }
    return allBlocks.some((pb: any) => expectedStatusTypes.includes(pb.statusType));
  }

  private getSelectedTransportOrderTargets(): TransportOrder[] {
    if (this.selectionType === SelectionTypes.TransportOrder) {
      return this.selectedTransportOrders || [];
    }

    const transportOrderTargets = new Map<string, TransportOrder>();

    if (this.selectionType === SelectionTypes.PlanningBlock) {
      (this.selectedPlanningBlocks || []).forEach((planningBlock) => {
        const transportOrderId = planningBlock?.transportOrderNumber;
        if (!transportOrderId) {
          return;
        }

        const existingTarget = transportOrderTargets.get(transportOrderId);
        if (existingTarget) {
          existingTarget.planningBlocks = [...(existingTarget.planningBlocks || []), planningBlock];
          return;
        }

        transportOrderTargets.set(transportOrderId, {
          id: transportOrderId,
          planningBlocks: [planningBlock]
        } as TransportOrder);
      });
    }

    if (this.selectionType === SelectionTypes.OrderLine) {
      (this.selectedOrderLines || []).forEach((orderLine) => {
        const transportOrderId = orderLine?.transportOrderId || orderLine?.transportOrderNo;
        if (!transportOrderId || transportOrderTargets.has(transportOrderId)) {
          return;
        }

        transportOrderTargets.set(transportOrderId, {
          id: transportOrderId
        } as TransportOrder);
      });
    }

    return Array.from(transportOrderTargets.values());
  }

  public hasSelectedTransportOrderTargets(): boolean {
    return this.getSelectedTransportOrderTargets().length > 0;
  }

  public shouldShowTransportOrderSection(): boolean {
    return this.selectionType === SelectionTypes.TransportOrder ||
      ((this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine) &&
        this.hasSelectedTransportOrderTargets());
  }

  public shouldShowPlanningBlockSection(): boolean {
    return this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine;
  }

  public isSingleColumnLayout(): boolean {
    return !(this.shouldShowTransportOrderSection() && this.shouldShowPlanningBlockSection());
  }

  private getTransportOrderIdsText(transportOrders: TransportOrder[]): string {
    return transportOrders.map(to => to.id).join(", ");
  }


  public canSendTo(): boolean {
    if (!this.hasSelectedTransportOrderTargets()) {
      return false;
    }

    const statusCheck = this.selectionType === SelectionTypes.PlanningBlock && this.resolvedTransportOrders.length
      ? this.hasResolvedTOPBStatuses([CLOSED], true)
      : this.hasPBStatuses([CLOSED], true);

    if (statusCheck) {
      return false;
    }
    return true;
  }

  public whyCantSendTo(): string {
    if (!this.hasSelectedTransportOrderTargets()) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_TRANSPORT_ORDER";
    }

    const statusCheck = this.selectionType === SelectionTypes.PlanningBlock && this.resolvedTransportOrders.length
      ? this.hasResolvedTOPBStatuses([CLOSED], true)
      : this.hasPBStatuses([CLOSED], true);

    if (statusCheck) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_SENDTO_CLOSED";
    }
  }

  public sendTo(): void {
    if (!this.canSendTo()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantSendTo());
      return;
    }

    const transportOrderTargets = this.getSelectedTransportOrderTargets();
    const toIdsText = this.getTransportOrderIdsText(transportOrderTargets);
    this.confirmWithSkip("sendTo", () => {
      const opId = this.startToOperation('sendTo', transportOrderTargets);
      this.planningActionService.sendTo(transportOrderTargets).subscribe({
        next: (updatedTOs) => {
          this.completePbOperation(opId, 'TO sent to haulier');
          this.refreshPlanningBoard("sendTo", updatedTOs);
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_SENDTO", {transportOrderId: toIdsText});
          this.refreshRequested.emit({source: "sendTo", transportOrders: updatedTOs});
        },
        error: (err) => {
          console.error("sendToToHaulier failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Send TO failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_SENDTO");
          this.refreshPlanningBoard("sendTo", transportOrderTargets);
          this.refreshRequested.emit({source: "sendTo", transportOrders: transportOrderTargets});
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_SENDTO", {transportOrderId: toIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_SENDTO"),
      items: transportOrderTargets,
      itemsType: "transportOrders",
      confirmButtonTextTranslationKey: "SHARED.SEND_TRANSPORT_ORDER"
    });
  }

  public canCancelTO(): boolean {
    if (this.isLocalSelectionTransportOrder()) {
      return false;
    }
    if (this.hasPBStatuses([IN_PROGRESS, CLOSED], false)) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED, PLANNED], true) && this.hasPBStatuses([PLANNED], false);
  }

  public whyCantCancelTO(): string {
    if (this.isLocalSelectionTransportOrder()) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_TRANSPORT_ORDER";
    }
    if (this.hasPBStatuses([IN_PROGRESS, CLOSED], false)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_UNPLANTO_INPROGRESS_OR_CLOSED";
    }
    if (!(this.hasPBStatuses([PRE_PLANNED, PLANNED], true) && this.hasPBStatuses([PLANNED], false)) ) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_CANCELTO_NOT_SENT";
    }
  }

  public cancelTO(): void {
    if (!this.canCancelTO()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantCancelTO());
      return;
    }
    const toIdsText = this.selectedTransportOrders.map(to => to.id).join(", ");
    this.confirmWithSkip("cancelTO", () => {
      // Save originals before cancel (cancel API strips truck info, needed for slot lookup)
      this._preCancelTOs = [...this.selectedTransportOrders];
      const opId = this.startToOperation('cancelTo', this.selectedTransportOrders);
      this.planningActionService.cancelTo(this.selectedTransportOrders).subscribe({
        next: (updatedTOs) => {
          this.completePbOperation(opId, 'TO cancelled');
          this.selectedTransportOrders = updatedTOs;
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_CANCELTO", {transportOrderId: toIdsText});
          this.refreshPlanningBoard("cancelTO", updatedTOs);
          this.refreshRequested.emit({source: "cancelTO", transportOrders: this.selectedTransportOrders});
        },
        error: (err) => {
          console.error("cancelTo failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Cancel TO failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_CANCELTO");
          this.refreshPlanningBoard("cancelTO", this.selectedTransportOrders);
          this.refreshRequested.emit({source: "cancelTO", transportOrders: this.selectedTransportOrders});
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_CANCELTO", {transportOrderId: toIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_CANCELTO"),
      items: this.selectedTransportOrders,
      itemsType: "transportOrders",
      confirmButtonTextTranslationKey: "SHARED.CANCEL_TRANSPORT_ORDER"
    });
  }

  public canUnplanTO(): boolean {
    if (this.isLocalSelectionTransportOrder()) {
      return false;
    }
    if (this.hasPBStatuses([IN_PROGRESS, CLOSED], false)) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED], true);
  }

  public whyCantUnplanTO(): string {
    if (this.isLocalSelectionTransportOrder()) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_TRANSPORT_ORDER";
    }
    if (this.hasPBStatuses([IN_PROGRESS, CLOSED], false)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_UNPLANTO_INPROGRESS_OR_CLOSED";
    }
    if (!this.hasPBStatuses([PRE_PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_UNPLAN_TO_NOT_CANCELLED";
    }
  }

  public unplanTO(): void {
    if (!this.canUnplanTO()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantUnplanTO());
      return;
    }

    const toIdsText = this.selectedTransportOrders.map(to => to.id).join(", ");
    const pbIds = this.selectedTransportOrders.map(to => to.planningBlocks).flat();

    this.confirmWithSkip("unplanTo", () => {
      const opId = this.startToOperation('unplanTo', this.selectedTransportOrders);
      this.planningActionService.unplanTo(this.selectedTransportOrders).subscribe({
        next: (updatedTOs) => {
          this.completePbOperation(opId, 'TO unplanned');
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_UNPLAN_TO", {transportOrderId: toIdsText});
          this.refreshPlanningBoard("unplanTo", this.selectedTransportOrders);
          this.refreshPbScreen(pbIds, true);
          this.refreshRequested.emit({source: "unplanTo", transportOrders: this.selectedTransportOrders});
        },
        error: (err) => {
          console.error("unplanTo failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Unplan TO failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_UNPLAN_TO");
          if (!this.isOptimistic) this.planningBoardFacade.stopLoadingOnBoard();
          this.refreshRequested.emit({source: "unplanTo", transportOrders: this.selectedTransportOrders});
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_UNPLAN_TO", {transportOrderId: toIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_UNPLAN_TO"),
      items: this.selectedTransportOrders,
      itemsType: "transportOrders",
      confirmButtonTextTranslationKey: "SHARED.UNPLAN_TRANSPORT_ORDER"
    });

  }

  public canCancelAndUnplanTO(): boolean {
    if (this.isLocalSelectionTransportOrder()) {
      return false;
    }
    if (this.hasPBStatuses([IN_PROGRESS, CLOSED], false)) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED, PLANNED], true) && this.hasPBStatuses([PLANNED], false);
  }

  public whyCantCancelAndUnplanTO(): string {
    if (this.isLocalSelectionTransportOrder()) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_TRANSPORT_ORDER";
    }
    if (this.hasPBStatuses([IN_PROGRESS, CLOSED], false)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_UNPLANTO_INPROGRESS_OR_CLOSED";
    }
    if (!(this.hasPBStatuses([PRE_PLANNED, PLANNED], true) && this.hasPBStatuses([PLANNED], false))) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_CANCELTO_NOT_SENT";
    }
  }

  public cancelAndUnplanTO(): void {
    if (!this.canCancelAndUnplanTO()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantCancelAndUnplanTO());
      return;
    }

    const toIdsText = this.selectedTransportOrders.map(to => to.id).join(", ");
    const pbIds = this.selectedTransportOrders.map(to => to.planningBlocks).flat();

    this.confirmWithSkip("cancelAndUnplanTO", () => {
      const opId = this.startToOperation('cancelAndUnplanTo', this.selectedTransportOrders);
      this.planningActionService.cancelTo(this.selectedTransportOrders).subscribe({
        next: (cancelledTos) => {
          this.planningActionService.unplanTo(cancelledTos).subscribe({
            next: (updatedTOs) => {
              this.completePbOperation(opId, 'TO cancelled & unplanned');
              if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_CANCEL_AND_UNPLAN_TO", {transportOrderId: toIdsText});
              this.refreshPlanningBoard("cancelAndUnplanTO", this.selectedTransportOrders);
              this.refreshPbScreen(pbIds, true);
              this.refreshRequested.emit({source: "cancelAndUnplanTO", transportOrders: this.selectedTransportOrders});
            },
            error: (err) => {
              console.error("cancelAndUnplanTO failed", err);
              this.failPbOperation(opId, this.extractErrorMessage(err, 'Cancel & Unplan TO failed'));
              if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_CANCEL_AND_UNPLAN_TO");
              if (!this.isOptimistic) this.planningBoardFacade.stopLoadingOnBoard();
              this.refreshRequested.emit({source: "cancelAndUnplanTO", transportOrders: this.selectedTransportOrders});
            },
          });
        },
        error: (err) => {
          console.error("cancelAndUnplanTO failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Cancel TO failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_CANCEL_AND_UNPLAN_TO");
          this.refreshRequested.emit({source: "cancelAndUnplanTO", transportOrders: this.selectedTransportOrders});
          this.planningBoardFacade.stopLoadingOnBoard();
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_CANCEL_AND_UNPLAN_TO", {transportOrderId: toIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_CANCEL_AND_UNPLAN_TO"),
      items: this.selectedTransportOrders,
      itemsType: "transportOrders",
      confirmButtonTextTranslationKey: "SHARED.CANCEL_AND_UNPLAN_TO"
    });
  }

  public canResendTransportOrder(): boolean {
    if (!this.hasSelectedTransportOrderTargets()) {
      return false;
    }
    const statusCheck = this.selectionType === SelectionTypes.PlanningBlock && this.resolvedTransportOrders.length
      ? this.hasResolvedTOPBStatuses([PLANNED, IN_PROGRESS], false)
      : this.hasPBStatuses([PLANNED, IN_PROGRESS], false);
    return statusCheck;
  }

  public whyCantResendTransportOrder(): string {
    if (!this.hasSelectedTransportOrderTargets()) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_TRANSPORT_ORDER";
    }
    const statusCheck = this.selectionType === SelectionTypes.PlanningBlock && this.resolvedTransportOrders.length
      ? this.hasResolvedTOPBStatuses([PLANNED, IN_PROGRESS], false)
      : this.hasPBStatuses([PLANNED, IN_PROGRESS], false);
    if (!statusCheck) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_RESEND_TO_NOT_SENT";
    }
  }

  public resendTransportOrder(): void {
    if (!this.canResendTransportOrder()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantResendTransportOrder());
      return;
    }

    const transportOrderTargets = this.getSelectedTransportOrderTargets();
    const toIdsText = this.getTransportOrderIdsText(transportOrderTargets);
    this.confirmWithSkip("resendTo", () => {
      const opId = this.startToOperation('resendTo', transportOrderTargets);
      this.planningActionService.resendTo(transportOrderTargets).subscribe({
        next: (updatedTOs) => {
          this.completePbOperation(opId, 'TO resent to haulier');
          this.refreshPlanningBoard("resendTo", updatedTOs);
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_RESEND_TO", {transportOrderId: toIdsText});
          this.refreshRequested.emit({source: "resendTo", transportOrders: updatedTOs});
        },
        error: (err) => {
          console.error("resendTo failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Resend TO failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_RESEND_TO");
          this.refreshPlanningBoard("resendTo", transportOrderTargets);
          this.refreshRequested.emit({source: "resendTo", transportOrders: transportOrderTargets});
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_RESEND_TO", {transportOrderId: toIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_RESEND_TO"),
      items: transportOrderTargets,
      itemsType: "transportOrders",
      confirmButtonTextTranslationKey: "SHARED.RESEND_TRANSPORT_ORDER"
    });
  }

  public canSendNextPb(): boolean {
    if (this.selectionType !== SelectionTypes.TransportOrder) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED], false);
  }

  public whyCantSendNextPb(): string {
    if (this.isLocalSelectionTransportOrder()) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_TRANSPORT_ORDER";
    }
    if (!this.hasPBStatuses([PRE_PLANNED], false)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_SEND_NEXT_PB_NOT_PLANNED";
    }
  }

  public sendNextPb(): void {
    if (!this.canSendNextPb()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantSendNextPb());
      return;
    }

    const toIdsText = this.selectedTransportOrders.map(to => to.id).join(", ");
    this.confirmWithSkip("sendNextPb", () => {
      const opId = this.startToOperation('sendNextPb', this.selectedTransportOrders);
      this.planningActionService.sendNextPb(this.selectedTransportOrders).subscribe({
        next: (updatedPbs) => {
          this.completePbOperation(opId, 'Next PB sent');
          this.refreshPlanningBoard("sendNextPb", null, updatedPbs);
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_SEND_NEXT_PB", {transportOrderId: toIdsText});
          this.refreshRequested.emit({source: "sendNextPb", transportOrders: this.selectedTransportOrders});
        },
        error: (err) => {
          console.error("sendNextPb failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Send next PB failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_SEND_NEXT_PB");
          this.refreshPlanningBoard("sendNextPb", this.selectedTransportOrders);
          this.refreshRequested.emit({source: "sendNextPb", transportOrders: this.selectedTransportOrders});
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_SEND_NEXT_PB", {transportOrderId: toIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_SEND_NEXT_PB"),
      items: this.selectedTransportOrders,
      itemsType: "transportOrders",
      confirmButtonTextTranslationKey: "SHARED.SEND_NEXT_PB"
    });

  }

  public canRemoveCapacity(): boolean {
    if (this.isLocalSelectionTransportOrder()) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED], true);
  }

  public whyCantRemoveCapacity(): string {
    if (this.isLocalSelectionTransportOrder()) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_TRANSPORT_ORDER";
    }
    if (!this.hasPBStatuses([PRE_PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_REMOVE_CAPACITY_NOT_PREPLANNED";
    }
  }

  public removeCapacity(): void {
    if (!this.canRemoveCapacity()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantRemoveCapacity());
      return;
    }

    const deletedCapacity: DeleteToFromCapacity[] = this.selectedTransportOrders.map(to => ({
      tranportOrderNo: to.id,
      truckName: to.truck?.name,
      driverName: to.driver
    }));

    const toIdsText = this.selectedTransportOrders.map(to => to.id).join(", ");
    this.confirmWithSkip("removeCapacity", () => {
      const opId = this.startToOperation('removeCapacity', this.selectedTransportOrders);
      this.planningActionService.removeCapacityFromTo(this.selectedTransportOrders).subscribe({
        next: (updatedTOs) => {
          this.completePbOperation(opId, 'Capacity removed');
          this.selectedTransportOrders = updatedTOs;
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_REMOVE_CAPACITY", {transportOrderId: toIdsText});
          this.refreshPlanningBoard("removeCapacity", this.selectedTransportOrders);
        },
        error: (err) => {
          console.error("removeCapacity failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Remove capacity failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_REMOVE_CAPACITY");
          this.refreshRequested.emit({source: "removeCapacity", transportOrders: this.selectedTransportOrders});
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_REMOVE_CAPACITY", {transportOrderId: toIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_REMOVE_CAPACITY"),
      items: this.selectedTransportOrders,
      itemsType: "transportOrders",
      confirmButtonTextTranslationKey: "SHARED.REMOVE_CAPACITY"

    });
  }

  public canSendPb(): boolean {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED], true);
  }

  public whyCantSendPb(): string {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_PB";
    }
    if (!this.hasPBStatuses([PRE_PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_SENDPB_NOT_PREPLANNED";
    }
  }

  public sendPb(): void {
    if (!this.canSendPb()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantSendPb());
      return;
    }
    const plIdsText = this.selectedPlanningBlocks.map(pb => pb.id).join(", ");
    this.confirmWithSkip("sendPB", () => {
      const opId = this.startPbOperation('sendPb', this.selectedPlanningBlocks);
      this.planningActionService.sendPb(this.selectedPlanningBlocks).subscribe({
        next: (updatedPbs) => {
          this.completePbOperation(opId, 'PB sent successfully');
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_SENDPB", {planningBlockId: plIdsText});
          this.refreshPlanningBoard("sendPb", null, updatedPbs);
          this.refreshRequested.emit({source: "sendPb", planningBlocks: updatedPbs});
        },
        error: (err) => {
          console.error("sendPb failed", err);
          this.failPbOperation(opId, this.extractErrorMessage(err, 'Send PB failed'));
          if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_SENDPB");
        }
      });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_SENDPB",
        {planningBlockId: plIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_SENDPB"),
      items: this.selectedPlanningBlocks,
      itemsType: "planningBlocks",
      confirmButtonTextTranslationKey: "SHARED.SEND_PB"
    });
  }

  public canCancelPb(): boolean {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return false;
    }
    return this.hasPBStatuses([PLANNED], true);
  }

  public whyCantCancelPb(): string {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_PB";
    }
    if (!this.hasPBStatuses([PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_CANCELPB_NOT_PLANNED";
    }
  }

  public cancelPb(): void {
    if (!this.canCancelPb()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantCancelPb());
      return;
    }

    const plIdsText = this.selectedPlanningBlocks.map(pb => pb.id).join(", ");
    this.confirmWithSkip("cancelPB", () => {
      let planningBlocks = this.selectedPlanningBlocks ?? [];
      const opId = this.startPbOperation('cancelPb', planningBlocks);

      this.planningActionService
        .cancelPb(planningBlocks)
        .subscribe({
          next: (updatedPbs) => {
            this.completePbOperation(opId, 'PB cancelled successfully');
            if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_CANCELPB", {planningBlockId: plIdsText});
            this.refreshPlanningBoard("cancelPb", null, updatedPbs);
            this.refreshRequested.emit({source: "cancelPb", planningBlocks: updatedPbs});
          },
          error: (err) => {
            console.error("cancelPb failed", err);
            this.failPbOperation(opId, this.extractErrorMessage(err, 'Cancel PB failed'));
            if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_CANCELPB");
          }
        });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_CANCELPB", {planningBlockId: plIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_CANCELPB"),
      items: this.selectedPlanningBlocks,
      itemsType: "planningBlocks",
      confirmButtonTextTranslationKey: "SHARED.CANCEL_PB"
    });
  }

  public canUnplanPb(): boolean {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED], true);
  }

  public whyCantUnplanPb(): string {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_PB";
    }
    if (!this.hasPBStatuses([PRE_PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_UNPLANPB_NOT_PREPLANNED";
    }
  }

  public unplanPb(): void {
    if (!this.canUnplanPb()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantUnplanPb());
      return;
    }

    const plIdsText = this.selectedPlanningBlocks.map(pb => pb.id).join(", ");
    this.confirmWithSkip("unplanPb", () => {
      let planningBlocks = this.selectedPlanningBlocks ?? [];
      const opId = this.startPbOperation('unplanPb', planningBlocks);

      this.planningActionService
        .unplanPb(planningBlocks)
        .subscribe({
          next: (updatedPbs) => {
            this.completePbOperation(opId, 'PB unplanned successfully');
            if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_UNPLANPB", {planningBlockId: plIdsText});
            this.refreshPlanningBoard("unplanPb", null, planningBlocks);
            this.refreshPbScreen(updatedPbs, false);
            this.refreshRequested.emit({source: "unplanPb", planningBlocks: planningBlocks});
          },
          error: (err) => {
            console.error("unplanPb failed", err);
            this.failPbOperation(opId, this.extractErrorMessage(err, 'Unplan PB failed'));
            if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_UNPLANPB");
          }
        });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_UNPLANPB", {planningBlockId: plIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_UNPLANPB"),
      items: this.selectedPlanningBlocks,
      itemsType: "planningBlocks",
      confirmButtonTextTranslationKey: "SHARED.UNPLAN_PB"
    });
  }

  public canCancelAndUnplanPb(): boolean {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return false;
    }
    return this.hasPBStatuses([PLANNED], true);
  }

  public whyCantCancelAndUnplanPb(): string {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_PB";
    }
    if (!this.hasPBStatuses([PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_CANCEL_UNPLANPB_NOT_PREPLANNED_OR_PLANNED";
    }
  }

  public cancelAndUnplanPb(): void {
    if (!this.canCancelAndUnplanPb()) {
      this.showToast(ActionsStatusResult.FailedPreConditions, this.whyCantCancelAndUnplanPb());
      return;
    }

    const plIdsText = this.selectedPlanningBlocks.map(pb => pb.id).join(", ");
    this.confirmWithSkip("cancelAndUnplanPb", () => {
      let planningBlocks = this.selectedPlanningBlocks ?? [];
      const opId = this.startPbOperation('cancelAndUnplanPb', planningBlocks);

      this.planningActionService
        .cancelPb(planningBlocks)
        .subscribe({
          next: (cancelledPbs) => {
            this.planningActionService
              .unplanPb(cancelledPbs)
              .subscribe({
                next: (updatedPbs) => {
                  this.completePbOperation(opId, 'PB cancelled & unplanned');
                  if (!this.isOptimistic) this.showToast(ActionsStatusResult.Success, "SHARED.PLANNING_ACTIONS.MESSAGE_SUCCEEDED_UNPLANPB", {planningBlockId: plIdsText});
                  this.refreshPlanningBoard("cancelAndUnplanPb", null, planningBlocks);
                  this.refreshPbScreen(updatedPbs, false);
                  this.refreshRequested.emit({source: "cancelAndUnplanPb", planningBlocks: updatedPbs});
                },
                error: (err) => {
                  console.error("cancelAndUnplanPb failed", err);
                  this.failPbOperation(opId, this.extractErrorMessage(err, 'Unplan PB failed'));
                  if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_UNPLANPB");
                }
              });
          },
          error: (err) => {
            console.error("cancelAndUnplanPb failed", err);
            this.failPbOperation(opId, this.extractErrorMessage(err, 'Cancel PB failed'));
            if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_CANCELPB");
          }
        });
    }, {
      message: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_MESSAGE_CANCEL_AND_UNPLANPB", {planningBlockId: plIdsText}),
      title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_CANCEL_AND_UNPLANPB"),
      items: this.selectedPlanningBlocks,
      itemsType: "planningBlocks",
      confirmButtonTextTranslationKey: "SHARED.CANCEL_AND_UNPLAN_PB"
    });
  }


  public canReplanPb(): boolean {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return false;
    }
    return this.hasPBStatuses([PRE_PLANNED], true);
  }

  public whyCantReplanPb(): string {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_PB";
    }
    if (!this.hasPBStatuses([PRE_PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_CANCEL_UNPLANPB_NOT_PREPLANNED_OR_PLANNED";
    }
  }

  public replanPb(): void {
    this.simpleModalService
      .addModal(PlanningactionsReplanModalComponent, {
        title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_REPLANPB"),
        planningBlocks: this.selectedPlanningBlocks,
        confirmButtonTextKey: "SHARED.REPLAN"
      })
      .subscribe((result: ReplanModalResult) => {
        if (!result || !result.confirmed) {
          return;
        }

        const planningBlocks: PlanningBlock[] = this.selectedPlanningBlocks || [];

        if (result.createNewTo) {
          this.replanToNewTransportOrder("replanPb", planningBlocks, result.newToPlanning, 'unplan');
          return;
        }

        // 1. Collect unique TO ids (from selected PBs + target TO from modal)
        const baseToIds = planningBlocks
          .map(pb => pb.transportOrderNumber)
          .filter(id => !!id);
        const opId = this.startPbOperation('replanPb', planningBlocks);
        if (!this.isOptimistic) this.planningBoardFacade.startLoadingOnBoard();
        // 2. Replan: service already performs a call per planning block (forkJoin)
        this.planningActionService
          .replanPb(result.targetTransportOrder.id, planningBlocks)
          .pipe(
             // 3. Once all replan calls are completed, load all affected TOs
            concatMap(() => this.refreshAffectedTransportOrders(baseToIds, [result.targetTransportOrder.id], planningBlocks)),
            tap(() => {
              this.completePbOperation(opId, 'PB replanned successfully');
              this.refreshRequested.emit({source: "replanPb", planningBlocks});
            }),
            catchError(err => {
              console.error("replanPb failed", err);
              this.failPbOperation(opId, err?.error?.message || err?.message || 'Replan PB failed');
              if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_UNPLANPB");
              return of(null);
            }),
            finalize(() => { if (!this.isOptimistic) this.planningBoardFacade.stopLoadingOnBoard(); })
          )
          .subscribe();
      });
  }

  public canCancelAndReplanPb(): boolean {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return false;
    }
    return this.hasPBStatuses([PLANNED], true);
  }

  public whyCantCancelAndReplanPb(): string {
    if (!(this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_BAD_SELECTION_PB";
    }
    if (!this.hasPBStatuses([PLANNED], true)) {
      return "SHARED.PLANNING_ACTIONS.MESSAGE_PRECONDITION_CANCEL_UNPLANPB_NOT_PREPLANNED_OR_PLANNED";
    }
  }

  public cancelAndReplanPb(): void {
    this.simpleModalService
      .addModal(PlanningactionsReplanModalComponent, {
        title: this.translate("SHARED.PLANNING_ACTIONS.CONFIRMATION_TITLE_CANCEL_AND_REPLANPB"),
        planningBlocks: this.selectedPlanningBlocks,
        confirmButtonTextKey: "SHARED.CANCEL_AND_REPLAN"
      })
      .subscribe((result: ReplanModalResult) => {
        if (!result || !result.confirmed) {
          return;
        }

        const planningBlocks: PlanningBlock[] = this.selectedPlanningBlocks || [];

        // Collect unique TO ids (from selected PBs + target TO from modal)
        if (result.createNewTo) {
          this.replanToNewTransportOrder("cancelAndReplanPb", planningBlocks, result.newToPlanning, 'cancelAndUnplan');
          return;
        }

        const baseToIds = planningBlocks
          .map(pb => pb.transportOrderNumber)
          .filter(id => !!id);
        const opId = this.startPbOperation('cancelAndReplanPb', planningBlocks);
        if (!this.isOptimistic) this.planningBoardFacade.startLoadingOnBoard();

        this.planningActionService
          .cancelPb(planningBlocks)
          .pipe(
            // Once cancel, unplan and replan are done, refresh all affected TOs
            concatMap(cancelled => this.planningActionService.replanPb(result.targetTransportOrder.id, cancelled)),
            concatMap(() => this.refreshAffectedTransportOrders(baseToIds, [result.targetTransportOrder.id], planningBlocks)),
            tap(() => {
              this.completePbOperation(opId, 'PB cancelled & replanned');
              this.refreshRequested.emit({source: "cancelAndReplanPb", planningBlocks});
            }),
            catchError(err => {
              console.error("cancelAndReplanPb failed", err);
              this.failPbOperation(opId, err?.error?.message || err?.message || 'Cancel & Replan PB failed');
              if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_UNPLANPB");
              return of(null);
            }),
            finalize(() => { if (!this.isOptimistic) this.planningBoardFacade.stopLoadingOnBoard(); })
          )
          .subscribe();
      });
  }

  private replanToNewTransportOrder(
    source: "replanPb" | "cancelAndReplanPb",
    planningBlocks: PlanningBlock[],
    newToPlanning: ReplanNewToPlanningRequest,
    preAction: 'unplan' | 'cancelAndUnplan'
  ): void {
    if (!newToPlanning) { return; }

    const originalToIds = Array.from(new Set<string>(
      planningBlocks
        .map(pb => pb.transportOrderNumber)
        .filter(id => !!id)
    ));
    const opId = this.startPbOperation(source, planningBlocks);
    if (!this.isOptimistic) this.planningBoardFacade.startLoadingOnBoard();

    this.runReplanPreAction(planningBlocks, preAction).pipe(
      concatMap((updatedPlanningBlocks: PlanningBlock[]) =>
        this.pbService.prePlanAction(
          updatedPlanningBlocks,
          newToPlanning.capacitySlot,
          newToPlanning.planDate.toISOString(),
          newToPlanning.driver,
          newToPlanning.haulier,
          !environment.disablePlanningGroupsCompatibilityCheck,
          newToPlanning.targetPlanningGroup,
          !newToPlanning.changePlanningBlockDate
        )
      ),
      concatMap((createdTransportOrders: TransportOrder[]) => {
        const createdTo = createdTransportOrders?.[0];
        const createdPlanningBlocks = (createdTo?.planningBlocks || []).filter(pb => !!pb);
        const createdToIds = createdTo?.id ? [createdTo.id] : [];
        const send$ = newToPlanning.sendAfterPreplan && createdPlanningBlocks.length
          ? this.planningActionService.sendPb(createdPlanningBlocks)
          : of(createdPlanningBlocks);
        const finalize$ = createdToIds.length
          ? this.pbService.updateFinalizedCombi(createdToIds, newToPlanning.finalizedCombi)
          : of([]);

        return send$.pipe(
          concatMap(() => finalize$),
          concatMap(() => this.refreshAffectedTransportOrders(originalToIds, createdToIds, planningBlocks)),
          map(() => ({createdToIds, createdPlanningBlocks}))
        );
      }),
      tap(() => {
        this.completePbOperation(opId, source === "replanPb" ? 'PB replanned successfully' : 'PB cancelled & replanned');
        this.refreshRequested.emit({source, planningBlocks});
      }),
      catchError(err => {
        console.error(`${source} to new TO failed`, err);
        this.failPbOperation(opId, err?.error?.message || err?.message || 'Replan PB to new TO failed');
        if (!this.isOptimistic) this.showToast(ActionsStatusResult.Failure, "SHARED.PLANNING_ACTIONS.MESSAGE_FAILED_UNPLANPB");
        return of(null);
      }),
      finalize(() => { if (!this.isOptimistic) this.planningBoardFacade.stopLoadingOnBoard(); })
    ).subscribe();
  }

  private runReplanPreAction(planningBlocks: PlanningBlock[], preAction: 'unplan' | 'cancelAndUnplan'): Observable<PlanningBlock[]> {
    if (preAction === 'cancelAndUnplan') {
      return this.planningActionService.cancelPb(planningBlocks).pipe(
        concatMap(cancelled => this.planningActionService.unplanPb(cancelled?.length ? cancelled : planningBlocks))
      );
    }

    return this.planningActionService.unplanPb(planningBlocks);
  }

  private refreshAffectedTransportOrders(originalToIds: string[], targetToIds: string[], planningBlocks: PlanningBlock[]): Observable<TransportOrder[]> {
    const uniqueOriginalToIds = Array.from(new Set<string>(originalToIds || []));
    const allToIds = Array.from(new Set<string>([...uniqueOriginalToIds, ...(targetToIds || [])].filter(id => !!id)));

    if (!allToIds.length) { return of([]); }

    return forkJoin(allToIds.map(toId =>
      this.planningActionService.getTransportOrderById(toId).pipe(
        catchError(err => {
          console.error("refreshAffectedTransportOrders failed", toId, err);
          return of(null);
        })
      )
    )).pipe(
      map((transportOrders: TransportOrder[]) => transportOrders || []),
      tap((transportOrders: TransportOrder[]) => {
        const visibleOriginalTos = transportOrders
          .filter(to => !!to && uniqueOriginalToIds.includes(to.id) && this.transportOrderStillVisible(to));

        transportOrders
          .filter(to => !!to)
          .forEach(to => this.planningBoardFacade.refreshToInPlanningBoard(to));

        if (uniqueOriginalToIds.length && visibleOriginalTos.length === 0) {
          this.planningBoardFacade.refreshOnUnplanPlanningActionForLastPlanningBlock(planningBlocks);
        }
      }),
      map((transportOrders: TransportOrder[]) => transportOrders.filter(to => !!to))
    );
  }

  private transportOrderStillVisible(transportOrder: TransportOrder): boolean {
    return !!transportOrder &&
      ((transportOrder.planningBlocks && transportOrder.planningBlocks.length > 0) ||
        !!(transportOrder as any).hasFuturePBs ||
        !!(transportOrder as any).hasPastPBs);
  }

  private confirmWithSkip(actionKey: string, action: () => void, options?: {
    title?: string;
    message?: string;
    items?: any[];
    itemsType?: "planningBlocks" | "transportOrders",
    confirmButtonTextTranslationKey?: string;

  }) {
    const storageKey = `skipConfirm:${actionKey}`;
    const skip = localStorage.getItem(storageKey) === "true";

    if (skip) {
      if (!this.isOptimistic) this.planningBoardFacade.startLoadingOnBoard();
      this.closeRequested.emit();
      this.onStart?.();
      action();
      return;
    }

    const title = options?.title ?? "Confirmation";
    const message = options?.message ?? "Are you sure?";

    const confirmButtonText = options?.confirmButtonTextTranslationKey ?? "CONFIRMATION_MODAL.OPTION_YES";

    this.simpleModalService
      .addModal(PlanningactionsConfirmationModalComponent, {
        title,
        messageTranslationKey: message,
        showDontAskAgain: true,
        rememberKey: actionKey,
        items: options.items,
        itemsType: options.itemsType || "auto",
        confirmButtonTextTranslationKey: confirmButtonText
      })
      .subscribe((result: PlanningactionsConfirmationModalResult) => {
        if (result && result.confirmed) {
          if (result.dontAskAgain) {
            localStorage.setItem(storageKey, "true");
          }
          if (!this.isOptimistic) this.planningBoardFacade.startLoadingOnBoard();
          this.closeRequested.emit();
          this.onStart?.();
          action();
        }
      });
  }

  private showToast(result: ActionsStatusResult, message: string, params?: object): void {
    switch (result) {
      case ActionsStatusResult.Success:
        this.userFeedbackService.showSuccessMessage(message, params);
        break;
      case ActionsStatusResult.Failure:
      case ActionsStatusResult.FailedPreConditions:
        this.userFeedbackService.showErrorMessage(message,
          params,
          undefined,
          this.stickyToaster);
        break;
      case ActionsStatusResult.BadSelection:
        this.userFeedbackService.showErrorMessage(message, params);
        break;
    }
  }

  private refreshPbScreen(planningBlocks?: PlanningBlock[], retrievePlanningBlocks: boolean = true) {
    if (retrievePlanningBlocks && planningBlocks && planningBlocks.length > 0) {
      const pbIds = planningBlocks.map(pb => pb.id);
      this.pbService.getPlanningBlocksByIds(pbIds).subscribe((updatedPlanningBlocks) => {
        this.pbActions.updatePlanningBlocksDisplay(updatedPlanningBlocks, true);
      });
    } else {
      this.pbActions.updatePlanningBlocksDisplay(planningBlocks,true);
    }
  }

  private refreshPlanningBoard(source: string, transportOrders?: TransportOrder[], planningBlocks?: PlanningBlock[]) {
    // In optimistic mode, skip the full-screen loading spinner but still
    // refresh board state and unplanned PB lists so the UI stays consistent.
    if (this.isOptimistic) {
      if ((source === "unplanTo" || source === "cancelAndUnplanTO" || source === "removeCapacity") && transportOrders?.length) {
        this.planningBoardFacade.refreshOnUnplanPlanningActionForTransportOrder(transportOrders);
        this.planningBoardFacade.storeUnplannedPlanningBlocks();
      } else if ((source === "unplanPb" || source === "cancelAndUnplanPb") && planningBlocks?.length) {
        const toId = planningBlocks[0]?.transportOrderNumber;
        if (toId) {
          this.planningActionService.getTransportOrderById(toId).subscribe((freshTo) => {
            if (!freshTo || !(freshTo as any).planningBlocks?.length) {
              this.planningBoardFacade.refreshOnUnplanPlanningActionForLastPlanningBlock(planningBlocks);
            } else {
              this.planningBoardFacade.refreshOnPlanningActionForTransportOrder(freshTo);
            }
          });
        } else {
          this.planningBoardFacade.refreshOnUnplanPlanningActionForLastPlanningBlock(planningBlocks);
        }
        this.planningBoardFacade.storeUnplannedPlanningBlocks();
      } else if ((source === "sendTo" || source === "resendTo") &&
        (this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine) &&
        transportOrders?.length) {
        forkJoin(
          transportOrders.map(transportOrder =>
            this.planningActionService.getTransportOrderById(transportOrder.id).pipe(
              tap((freshTo) => {
                if (freshTo) {
                  const matchingPlanningBlocks = (this.selectedPlanningBlocks || [])
                    .filter(pb => pb.transportOrderNumber === transportOrder.id);
                  this.planningBoardFacade.refreshOnPlanningActionForPlanningBlock(freshTo, matchingPlanningBlocks);
                }
              }),
              catchError(() => of(null))
            )
          )
        ).subscribe();
        this._preCancelTOs = null;
      } else if (transportOrders?.length) {
        // cancelTO, sendTo, resendTo — TO stays on slot but status changes
        // For cancel: the API strips the truck, so we need to remove the old TO first
        // then re-add with fresh data. For send/resend: update in place.
        // The cancel/send/resend API may return incomplete TO data (e.g. cancel strips truck).
        // Re-fetch the TO by ID to get the full, correct data for board display.
        const toId = transportOrders[0]?.id;
        if (toId) {
          this.planningActionService.getTransportOrderById(toId).subscribe((freshTo) => {
            if (freshTo) {
              this.planningBoardFacade.refreshOnPlanningActionForTransportOrder(freshTo);
            }
          });
        }
        this._preCancelTOs = null;
      } else if (planningBlocks?.length) {
        // cancelPb, sendPb — PB stays on slot but status changes.
        // Re-fetch and refresh immediately so status/send markers update without monitor lag.
        const toId = planningBlocks[0]?.transportOrderNumber;
        if (toId) {
          this.planningActionService.getTransportOrderById(toId).subscribe((freshTo) => {
            if (freshTo) {
              this.planningBoardFacade.refreshOnPlanningActionForTransportOrder(freshTo);
            } else {
              this.planningBoardFacade.clearSelection();
            }
          });
        } else {
          this.planningBoardFacade.clearSelection();
        }
      }
      return;
    }

    if ((source === "unplanTo" || source === "cancelAndUnplanTO" || source === "removeCapacity") && transportOrders && transportOrders.length > 0) {
      this.planningBoardFacade.refreshOnUnplanPlanningActionForTransportOrder(transportOrders);
      this.planningBoardFacade.storeUnplannedPlanningBlocks();
    } else if (
      (source === "unplanPb" || source === "cancelAndUnplanPb") ||
      (source === "sendNextPb" && planningBlocks && planningBlocks.length > 0)) {

      const toId = planningBlocks[0].transportOrderNumber;
      this.planningActionService.getTransportOrderById(toId).subscribe((to: TransportOrder) => {
        if (!to) {
          this.planningBoardFacade.refreshOnUnplanPlanningActionForLastPlanningBlock(planningBlocks);
        } else {
          this.planningBoardFacade.refreshOnPlanningActionForTransportOrder(to);
        }
      });
      this.planningBoardFacade.storeUnplannedPlanningBlocks();
    } else if ((source === "sendTo" || source === "resendTo") &&
      (this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine) &&
      transportOrders && transportOrders.length > 0) {
      forkJoin(
        transportOrders.map(transportOrder =>
          this.planningActionService.getTransportOrderById(transportOrder.id).pipe(
            tap((freshTo: TransportOrder) => {
              if (freshTo) {
                const matchingPlanningBlocks = (this.selectedPlanningBlocks || [])
                  .filter(pb => pb.transportOrderNumber === transportOrder.id);
                this.planningBoardFacade.refreshOnPlanningActionForPlanningBlock(freshTo, matchingPlanningBlocks);
              }
            }),
            catchError(() => of(null))
          )
        )
      ).subscribe();
    } else if (this.selectionType === SelectionTypes.TransportOrder) {
      this.planningBoardFacade.refreshOnPlanningActionForTransportOrder(transportOrders[0] as any);
    } else {
      if (planningBlocks && planningBlocks.length > 0) {
        const toId = planningBlocks[0].transportOrderNumber;
        this.planningActionService.getTransportOrderById(toId).subscribe((to: TransportOrder) => {
          this.planningBoardFacade.refreshOnPlanningActionForPlanningBlock(to, planningBlocks);
        });
      }
    }
  }

  private isLocalSelectionTransportOrder(): boolean {
    return this.selectionType !== SelectionTypes.TransportOrder || !this.selectedTransportOrders || this.selectedTransportOrders.length === 0;
  }
}
